import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BalanceMatchingBanner } from "components/DesignSystem/BalanceMatchingBanner/BalanceMatchingBanner";
import { Button } from "components/DesignSystem/Button/Button";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { Header } from "components/DesignSystem/Header/Header";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { GeneralLedgerFilters } from "components/GeneralLedger/GeneralLedgerFilters";
import { TrialBalanceView } from "components/GeneralLedger/TrialBalanceView/TrialBalanceView";
import { ExportLedger } from "components/GeneralLedger/ExportLedger";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import { StartBookkeepingDateEmptyScreen } from "components/GeneralLedger/GeneralLedgerEmptyScreen";
import { ArrowRight } from "components/icons/ArrowRight";
import { ChatTeardropDots } from "components/icons/ChatTeardropDots";
import { ACCOUNTING_METHOD_TYPES, TRIAL_BALANCE } from "constants/bookkeeping";
import { PERIOD } from "constants/revenueMetrics";
import { AnimatePresence, motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useDispatch, useSelector } from "react-redux";
import { useGetGeneralLedgerQuery } from "store/apis/generalLedger";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import { LedgerReportType } from "store/slices/generalLedger";
import { setOpenComment } from "store/slices/openComment";
import { RootState } from "store/store";
import { formatDate } from "utils/formatDate";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";

const TrialBalanceViewAsFilter = ({
  groupBy,
  onGroupByChange,
}: {
  groupBy: string | null;
  onGroupByChange: (value: string | null) => void;
}) => {
  const label = groupBy === PERIOD.MONTHLY ? "Months" : "None";

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger className="all:unset t-group t-self-center">
        <Button size="xsmall">
          <span className="t-text-text-30">View as:</span>
          <span className="t-ml-1 t-text-text-60">{label}</span>
          <div className="t-text-text-30 t-transition-transform t-duration-200 t-rotate-90 group-data-[state=open]:-t-rotate-90">
            <ArrowRight color="currentColor" size="22" stroke="1.25" />
          </div>
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          sideOffset={8}
          className="!t-min-w-[10rem]"
          align="start"
        >
          <Dropdown.Item
            onSelect={() => onGroupByChange(null)}
            className={!groupBy ? "t-bg-purple-0" : ""}
          >
            None
          </Dropdown.Item>
          <Dropdown.Item
            onSelect={() => onGroupByChange(PERIOD.MONTHLY)}
            className={groupBy === PERIOD.MONTHLY ? "t-bg-purple-0" : ""}
          >
            Months
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export const TrialBalancePage = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { updateMultiple } = useUpdateQuery();
  const query = useQuery();
  const dispatch = useDispatch();
  const commentPane = useModal();
  const openComment = useAppSelector((state) => state.openComment.openComment);

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.category) {
      dispatch(setOpenComment(comment.category.uuid));
    }
  };

  const groupByFromQuery = query.get("groupBy");

  const { groupBy: groupByFromStore } = useSelector(
    (state: RootState) => state.ledgerFilter
  );

  const groupBy = groupByFromQuery || groupByFromStore;

  const {
    filters: {
      cashFlow,
      minAmount,
      maxAmount,
      accountingMethod,
      startDate,
      endDate,
      hideZero,
      categoryIds,
    },
  } = useSelector((state: RootState) => state.ledgerFilter);

  const filterValues = {
    cashFlow,
    minAmount: ((minAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    maxAmount: ((maxAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    accountingMethod,
    startDate,
    endDate,
    hideZero,
    categoryIds,
    reportType: TRIAL_BALANCE as LedgerReportType,
  };

  const {
    data: generalLedgerRes,
    isFetching,
    isSuccess,
  } = useGetGeneralLedgerQuery(
    {
      groupId,
      entityId,
      comparison_ledger_type: groupBy,
      ...filterValues,
    },
    { skip: !groupId || !entityId, refetchOnMountOrArgChange: true }
  );

  const { ledger_data: generalLedgerData, balance_summary } =
    generalLedgerRes || {};

  const isEmpty = !generalLedgerData || generalLedgerData.length === 0;

  const handleGroupByChange = (value: string | null) => {
    updateMultiple([{ query: "groupBy", value }]);
  };

  return (
    <DashboardContainer className="t-h-full sm:t-h-auto sm:t-overflow-x-visible">
      <DashboardContainer.Header className="t-px-10 sm:t-px-3">
        <Header
          v2
          title="Trial Balance"
          right={
            <div className="t-flex t-gap-1 t-mt-1">
              <ExportLedger reportType={TRIAL_BALANCE} size="xsmall" />
              <Button
                customType="icon"
                onClick={commentPane.toggle}
                size="xsmall"
              >
                <ChatTeardropDots />
              </Button>
            </div>
          }
        />
      </DashboardContainer.Header>

      <DashboardContainer.Content
        style={{ overflowY: "hidden" }}
        className="sm:!t-overflow-visible"
      >
        <motion.div
          layout
          className="t-flex t-h-full t-pl-5 sm:t-h-auto sm:t-pl-3"
        >
          <div className="t-relative t-gap-5 t-flex t-flex-col t-flex-1 t-min-w-0 t-overscroll-y-contain">
            <DashboardContainer.Header
              sticky
              className={classNames("t-flex t-flex-col t-gap-4", {
                "t-pr-10 sm:t-pr-3 sm:!t-static": !commentPane.isOpen,
                "t-pr-3 sm:!t-static": commentPane.isOpen,
              })}
            >
              <div className="t-bg-surface t-flex t-flex-col t-gap-4">
                <div className="t-flex t-gap-2 t-w-full t-items-center t-flex-wrap">
                  <TrialBalanceViewAsFilter
                    groupBy={groupBy}
                    onGroupByChange={handleGroupByChange}
                  />
                  <GeneralLedgerFilters entityId={entityId} />
                </div>

                {balance_summary && (
                  <BalanceMatchingBanner
                    state={
                      balance_summary.difference === 0 ? "success" : "error"
                    }
                    statusText={
                      balance_summary.difference === 0
                        ? "Totals matching"
                        : "Totals not matching"
                    }
                    formulaText={
                      balance_summary.difference === 0
                        ? "Debits = Credits"
                        : "Debits ≠ Credits"
                    }
                    dateRange={`${formatDate(startDate.value)} - ${formatDate(
                      endDate.value
                    )}`}
                  >
                    <div className="t-flex t-flex-col t-gap-1">
                      <span className="t-text-text-30 t-text-body-sm">
                        Total Debits
                      </span>
                      <AmountSuperScript
                        amount={balance_summary.total_debits || 0}
                      />
                    </div>
                    <span className="t-self-center t-text-body-lg t-text-text-30">
                      {balance_summary.difference === 0 ? "=" : "≠"}
                    </span>
                    <div className="t-flex t-flex-col t-gap-1">
                      <span className="t-text-text-30 t-text-body-sm">
                        Total Credits
                      </span>
                      <AmountSuperScript
                        amount={balance_summary.total_credits || 0}
                      />
                    </div>
                  </BalanceMatchingBanner>
                )}

                <AccountingMethodBanner
                  selectedAccountingMethod={
                    filterValues.accountingMethod.value ??
                    (query.get("accountingMethod") as ACCOUNTING_METHOD_TYPES)
                  }
                  entityId={entityId}
                />
              </div>
            </DashboardContainer.Header>

            <div
              className={classNames("t-overflow-x-auto t-pb-24", {
                "t-pr-6 sm:t-pr-3": !commentPane.isOpen,
                "t-pr-3": commentPane.isOpen,
                "t-h-full": isEmpty,
              })}
            >
              <Async.Root
                isLoading={isFetching}
                isEmpty={isEmpty}
                isSuccess={isSuccess}
              >
                <Async.Empty>
                  <StartBookkeepingDateEmptyScreen />
                </Async.Empty>
                <Async.Success>
                  <TrialBalanceView
                    data={generalLedgerData}
                    groupBy={groupBy}
                  />
                </Async.Success>
              </Async.Root>
            </div>
          </div>

          <AnimatePresence>
            {commentPane.isOpen && (
              <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-sticky t-top-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
                <CommentsSlider
                  activeComment={openComment}
                  setActiveComment={setActiveComment}
                  onClose={commentPane.toggle}
                  commentTypes={[COMMENT_TYPE.GENERAL_LEDGER_COMMENT]}
                  generalLedgerFilters={{
                    cashFlow,
                    minAmount: ((minAmount.value || "") as string)
                      ?.replaceAll("$", "")
                      .replaceAll(",", ""),
                    maxAmount: ((maxAmount.value || "") as string)
                      ?.replaceAll("$", "")
                      .replaceAll(",", ""),
                    accountingMethod,
                    startDate,
                    endDate,
                    hideZero,
                    categoryIds,
                    reportType: TRIAL_BALANCE as LedgerReportType,
                  }}
                />
              </SliderAnimation>
            )}
          </AnimatePresence>
        </motion.div>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};
